import { getQuestScheduleDateKey } from './questScheduleDate';

const DAY_MS = 24 * 60 * 60 * 1000;
const defaultTranslate = (key, fallback = key, replacements = null) => {
  if (typeof fallback !== 'string' || !replacements || typeof replacements !== 'object') {
    return fallback;
  }

  return fallback.replace(/\{(\w+)\}/g, (_, token) => {
    const replacement = replacements[token];
    return replacement === undefined || replacement === null ? `{${token}}` : String(replacement);
  });
};

const WEEKDAY_TO_ISO = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

const ALLOWED_MOVE_SWAP_TYPES = new Set(['Meal', 'Workout', 'Mindfulness']);

function createInternalDateFormatter(timezone, options = {}) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone || 'UTC',
    ...options,
  });
}

function createDisplayDateFormatter(locale, timezone, options = {}) {
  return new Intl.DateTimeFormat(locale || 'en-US', {
    timeZone: timezone || 'UTC',
    ...options,
  });
}

export function getLocalDateKey(dateValue, timezone) {
  if (!dateValue) {
    return null;
  }

  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone || 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const parts = formatter.formatToParts(date);
  const acc = { year: '1970', month: '01', day: '01' };
  for (const part of parts) {
    if (part.type === 'year') acc.year = part.value;
    if (part.type === 'month') acc.month = part.value;
    if (part.type === 'day') acc.day = part.value;
  }
  return `${acc.year}-${acc.month}-${acc.day}`;
}

export function localDayTimestampFromKey(dateKey) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || ''))) {
    return null;
  }
  const [year, month, day] = String(dateKey).split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

export function getLocalIsoWeekday(dateValue, timezone) {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return 1;
  }
  const weekday = createInternalDateFormatter(timezone, { weekday: 'long' }).format(date).toLowerCase();
  return WEEKDAY_TO_ISO[weekday] || 1;
}

export function getCurrentLocalWeekDays({ now = new Date(), timezone, locale = 'en-US' } = {}) {
  const todayKey = getLocalDateKey(now, timezone);
  const todayTs = localDayTimestampFromKey(todayKey);
  const weekday = getLocalIsoWeekday(now, timezone);
  const startOfWeekTs = todayTs - (weekday - 1) * DAY_MS;
  const dayFormatter = createDisplayDateFormatter(locale, timezone, { weekday: 'short' });
  const dateFormatter = createDisplayDateFormatter(locale, timezone, { month: 'short', day: 'numeric' });
  const dayNumberFormatter = createDisplayDateFormatter(locale, timezone, { day: 'numeric' });

  return Array.from({ length: 7 }, (_, index) => {
    const dayTs = startOfWeekTs + index * DAY_MS;
    const dayDate = new Date(dayTs);
    const dateKey = getLocalDateKey(dayDate, timezone);
    return {
      dateKey,
      timestamp: dayTs,
      dayName: dayFormatter.format(dayDate),
      shortLabel: `${dayFormatter.format(dayDate)} ${dateFormatter.format(dayDate)}`,
      dayNum: dayNumberFormatter.format(dayDate),
      isToday: dateKey === todayKey,
    };
  });
}

function normalizeQuestType(type) {
  return typeof type === 'string' ? type.trim() : '';
}

function isAllowedQuestType(type) {
  return ALLOWED_MOVE_SWAP_TYPES.has(normalizeQuestType(type));
}

function isSameCurrentLocalWeek(dateKey, weekDays) {
  return weekDays.some((day) => day.dateKey === dateKey);
}

function findQuestByDayAndMatcher(quests, dayKey, matcher) {
  return quests.find((quest) => getQuestScheduleDateKey(quest) === dayKey && matcher.matches(quest)) || null;
}

export function isQuestEligibleForMoveSwap({ quest, timezone, now = new Date() }) {
  if (!quest || quest.completed || quest.isLockedTeaser) {
    return false;
  }

  const type = normalizeQuestType(quest.type);
  if (!isAllowedQuestType(type)) {
    return false;
  }

  const weekDays = getCurrentLocalWeekDays({ now, timezone });
  const questDateKey = getQuestScheduleDateKey(quest);
  return Boolean(questDateKey) && isSameCurrentLocalWeek(questDateKey, weekDays);
}

function buildMealOption({ quest, allQuests, day }) {
  const mealType = String(quest.mealType || '').toLowerCase();
  const targetQuest = findQuestByDayAndMatcher(allQuests, day.dateKey, {
    timezone: day.timezone,
    matches: (candidate) => normalizeQuestType(candidate.type) === 'Meal' && String(candidate.mealType || '').toLowerCase() === mealType,
  });

  if (!targetQuest) {
    return { enabled: false, reason: `No ${mealType || 'matching'} quest` };
  }

  if (targetQuest.completed) {
    return { enabled: false, reason: 'Completed quest' };
  }

  return {
    enabled: true,
    action: 'swap',
    targetQuestId: targetQuest._id || targetQuest.id,
    targetQuestName: targetQuest.name,
  };
}

function buildMindfulnessOption({ allQuests, day }) {
  const targetQuest = findQuestByDayAndMatcher(allQuests, day.dateKey, {
    timezone: day.timezone,
    matches: (candidate) => normalizeQuestType(candidate.type) === 'Mindfulness',
  });

  if (!targetQuest) {
    return { enabled: false, reason: 'No mindfulness quest' };
  }

  if (targetQuest.completed) {
    return { enabled: false, reason: 'Completed quest' };
  }

  return {
    enabled: true,
    action: 'swap',
    targetQuestId: targetQuest._id || targetQuest.id,
    targetQuestName: targetQuest.name,
  };
}

function buildWorkoutOption({ quest, allQuests, day, sourceDayTimestamp }) {
  const targetQuest = findQuestByDayAndMatcher(allQuests, day.dateKey, {
    timezone: day.timezone,
    matches: (candidate) => normalizeQuestType(candidate.type) === 'Workout',
  });

  if (targetQuest) {
    if (targetQuest.completed) {
      return { enabled: false, reason: 'Completed workout' };
    }

    return {
      enabled: true,
      action: 'swap',
      targetQuestId: targetQuest._id || targetQuest.id,
      targetQuestName: targetQuest.name,
    };
  }

  if (day.timestamp <= sourceDayTimestamp) {
    return { enabled: false, reason: 'Earlier empty day' };
  }

  return {
    enabled: true,
    action: 'move',
    targetQuestId: null,
    targetQuestName: null,
  };
}

export function buildMoveSwapDayOptions({ quest, allQuests, timezone, locale = 'en-US', now = new Date() }) {
  const weekDays = getCurrentLocalWeekDays({ now, timezone, locale }).map((day) => ({ ...day, timezone }));
  const sourceDateKey = getLocalDateKey(quest?.start || quest?.date, timezone);
  const sourceDayTimestamp = localDayTimestampFromKey(sourceDateKey);

  return weekDays.map((day) => {
    if (day.dateKey === sourceDateKey) {
      return {
        ...day,
        enabled: false,
        reason: 'Current day',
      };
    }

    const type = normalizeQuestType(quest?.type);
    let nextState;
    if (type === 'Meal') {
      nextState = buildMealOption({ quest, allQuests, day });
    } else if (type === 'Mindfulness') {
      nextState = buildMindfulnessOption({ allQuests, day });
    } else if (type === 'Workout') {
      nextState = buildWorkoutOption({ quest, allQuests, day, sourceDayTimestamp });
    } else {
      nextState = { enabled: false, reason: 'Unavailable' };
    }

    return {
      ...day,
      ...nextState,
    };
  });
}

export function getMoveSwapOptionLabel(option, t = defaultTranslate) {
  if (!option || !option.enabled) {
    return '';
  }

  if (option.action === 'move') {
    return t('moveSwap.optionMoveHere', 'Move here');
  }

  return t('moveSwap.optionSwapWith', 'Swap with {target}', {
    target: option.targetQuestName || '',
  });
}

export function buildMoveSwapConfirmationCopy(option, t = defaultTranslate) {
  if (!option) {
    return '';
  }

  if (option.action === 'move') {
    return t('moveSwap.moveTo', 'Move to {target}?', { target: option.shortLabel });
  }

  return t('moveSwap.swapWith', 'Swap with {target}?', { target: option.shortLabel });
}

function decorateQuestDate(quest, timezone) {
  return {
    ...quest,
    date: getQuestScheduleDateKey(quest) || getLocalDateKey(quest.start || quest.date, timezone),
  };
}

export function applyOptimisticQuestMoveSwap({ quests, sourceQuestId, option, timezone }) {
  if (!Array.isArray(quests) || !option) {
    return Array.isArray(quests) ? quests : [];
  }

  const sourceQuest = quests.find((quest) => (quest._id || quest.id) === sourceQuestId);
  if (!sourceQuest) {
    return quests;
  }

  if (option.action === 'swap') {
    const targetQuest = quests.find((quest) => (quest._id || quest.id) === option.targetQuestId);
    if (!targetQuest) {
      return quests;
    }

    return quests.map((quest) => {
      const questId = quest._id || quest.id;
      if (questId === sourceQuestId) {
        return decorateQuestDate({
          ...quest,
          start: targetQuest.start,
          end: targetQuest.end,
          scheduledLocalDate: targetQuest.scheduledLocalDate || getQuestScheduleDateKey(targetQuest),
          scheduledLocalStartMinutes: targetQuest.scheduledLocalStartMinutes,
          scheduledLocalEndMinutes: targetQuest.scheduledLocalEndMinutes,
          userTimezoneAtScheduling: targetQuest.userTimezoneAtScheduling || timezone,
        }, timezone);
      }
      if (questId === option.targetQuestId) {
        return decorateQuestDate({
          ...quest,
          start: sourceQuest.start,
          end: sourceQuest.end,
          scheduledLocalDate: sourceQuest.scheduledLocalDate || getQuestScheduleDateKey(sourceQuest),
          scheduledLocalStartMinutes: sourceQuest.scheduledLocalStartMinutes,
          scheduledLocalEndMinutes: sourceQuest.scheduledLocalEndMinutes,
          userTimezoneAtScheduling: sourceQuest.userTimezoneAtScheduling || timezone,
        }, timezone);
      }
      return quest;
    });
  }

  const sourceDayTs = localDayTimestampFromKey(getLocalDateKey(sourceQuest.start || sourceQuest.date, timezone));
  const targetDayTs = localDayTimestampFromKey(option.dateKey);
  if (sourceDayTs === null || targetDayTs === null) {
    return quests;
  }
  const shiftMs = targetDayTs - sourceDayTs;

  return quests.map((quest) => {
    const questId = quest._id || quest.id;
    if (questId !== sourceQuestId) {
      return quest;
    }
    return decorateQuestDate({
      ...quest,
      start: new Date(new Date(quest.start).getTime() + shiftMs).toISOString(),
      end: new Date(new Date(quest.end).getTime() + shiftMs).toISOString(),
      scheduledLocalDate: option.dateKey,
      userTimezoneAtScheduling: quest.userTimezoneAtScheduling || timezone,
    }, timezone);
  });
}

export function decorateCalendarQuest(quest, questIcons, timezone) {
  const type = quest.type || 'Workout';
  return {
    ...quest,
    id: quest._id || quest.id,
    date: getQuestScheduleDateKey(quest) || getLocalDateKey(quest.start, timezone),
    icon: questIcons[type] || questIcons.default,
  };
}
